/**
 * Come si chiamano e come si scrivono gli attributi dei CI.
 *
 * ## Perché in un modulo solo
 *
 * Gli attributi compaiono in due punti: come **colonne** nell'elenco dei CI e
 * come **righe** nella scheda di dettaglio. Le due pagine avevano ciascuna la
 * propria tabella di etichette, ed erano già in disaccordo: `host_count` era
 * «Su host» in elenco e «Presente su host» nel dettaglio — due nomi per lo
 * stesso dato, in due pagine che si aprono una di seguito all'altra. Non è un
 * errore che rompe qualcosa, ed è proprio per questo che sarebbe rimasto.
 *
 * ## Etichetta lunga ed etichetta di colonna
 *
 * Le due rese hanno un vincolo diverso: una riga di dettaglio ha tutta la
 * larghezza che serve, l'intestazione di una colonna no — «Spazio provisionato
 * (GB)» sopra una colonna di numeri allarga la tabella oltre lo schermo. Le
 * poche etichette che vanno accorciate stanno in {@link COLUMN_LABELS}; per
 * tutte le altre `columnLabel` e `attributeLabel` danno la stessa cosa, che è
 * il caso normale e quello che si vuole.
 */

import { localeCorrente, t } from './i18n/lingua'

/** Nome tecnico → etichetta leggibile, per gli attributi che ricorrono. */
export const ATTRIBUTE_LABELS: Record<string, string> = {
  // I nomi comuni a quasi tutte le classi. Mancavano, quindi comparivano
  // ripuliti dall'inglese — «Vendor», «Model», «Serial», «Os version» — in
  // mezzo a colonne in italiano, su una tabella che il Control Plane etichetta
  // in italiano nel proprio vocabolario. Due tabelle di etichette che
  // divergono sono la ragione per cui questo file esiste.
  vendor: t('Produttore'),
  mac_vendor: t('Produttore scheda'),
  account: t('Account'),
  cloud_cost_month_to_date: t('Costo del mese'),
  cloud_cost_currency: t('Valuta dei costi'),
  cloud_cost_by_service: t('Costi per servizio'),
  cloud_cost_period_start: t('Costi: inizio periodo'),
  cloud_cost_period_end: t('Costi: fine periodo'),
  virtualization_hint: t('Indizio di virtualizzazione'),
  model: t('Modello'),
  serial: t('Serial Number'),
  // Il seriale del telaio **in cui si sta**, non il proprio: lo dichiara un
  // controller di gestione fuori banda, e appartiene al server accanto.
  chassis_serial: t('Seriale del telaio'),

  // --- Gruppi di continuità ---
  //
  // Mancavano **tutti**: il Control Plane li etichetta in italiano nel proprio
  // vocabolario e la console li rendeva con la ricaduta automatica, cioè
  // «Ups battery charge» in mezzo a colonne in italiano. È esattamente il
  // disaccordo fra due tabelle che questo file esiste per impedire, e nessuno
  // se n'era accorto perché la ricaduta non fallisce: produce una parola.
  // --- Ciò che ntopng dice di un host ---
  //
  // Sono giudizi di ntopng, riportati e non rivalutati. Il punteggio resta un
  // numero: non ha scala dichiarata né massimo, e trasformarlo qui in un colore
  // gli darebbe un'autorevolezza che non ha nessuno.
  ntopng_score: t('Punteggio ntopng'),
  ntopng_alerts: t('Allarmi su ntopng'),
  ntopng_blacklisted: t('In blacklist secondo ntopng'),
  ntopng_flagged: t('Segnalato da ntopng'),
  // I motivi in chiaro e non un livello: «ha allarmi aperti su ntopng» dice
  // dove andare a guardare, «rischio: alto» non dice niente e va creduto.
  ntopng_flag_reasons: t('Perché è segnalato'),
  ntopng_last_seen: t('Visto da ntopng'),
  ntopng_apps: t('Protocolli applicativi'),
  detection_method: t('Come è stato rilevato'),

  ups_output_source: t('Alimentazione in uso'),
  ups_battery_status: t('Stato della batteria'),
  ups_battery_charge: t('Carica della batteria'),
  ups_battery_runtime: t('Autonomia stimata'),
  ups_battery_voltage: t('Tensione della batteria'),
  ups_battery_temperature: t('Temperatura della batteria'),
  ups_seconds_on_battery: t('Da quanto è a batteria'),
  ups_input_voltage: t('Tensione in ingresso'),
  ups_input_frequency: t('Frequenza in ingresso'),
  ups_input_line_bads: t('Mancanze di rete'),
  ups_output_voltage: t('Tensione in uscita'),
  ups_output_frequency: t('Frequenza in uscita'),
  ups_output_power: t('Potenza in uscita'),
  ups_output_load: t('Carico in uscita'),
  ups_power_capacity: t('Potenza nominale'),
  ups_alarms: t('Allarmi attivi'),
  ups_attached_devices: t('Apparati collegati'),

  // Ciò che la MIB standard non ha e che arriva dal costruttore. L'età in anni
  // è la data nella forma in cui la si usa: «03/14/19» richiede un conto a chi
  // guarda, e chi guarda un UPS alle tre di notte non lo fa.
  ups_battery_last_replaced: t('Batterie sostituite il'),
  ups_battery_age_years: t('Età delle batterie'),
  ups_manufacture_date: t('Data di fabbricazione'),
  ups_self_test_date: t('Ultimo autotest'),
  ups_battery_packs: t('Pacchi batteria'),
  // Il numero che dice «sostituisci adesso» prima che l'autonomia crolli: la
  // carica resta al 100% fino all'ultimo.
  ups_battery_packs_bad: t('Pacchi batteria guasti'),
  ups_battery_nominal_voltage: t('Tensione nominale della batteria'),
  ups_battery_current: t('Corrente della batteria'),
  // Del locale, non della batteria: due sonde diverse e due rimedi diversi.
  ups_ambient_temperature: t('Temperatura ambiente'),
  // Derivati dal Control Plane seguendo le relazioni: l'indirizzo del
  // controller fuori banda sta sul CI del controller, il numero di macchine
  // virtuali si conta sulle relazioni. Sul server ci sono perché la ricerca, i
  // filtri e l'esportazione verso un ITSM non sanno seguire una relazione.
  bmc_ip: t('Indirizzo del controller di gestione'),
  // Viste **da dentro**, e per questo distinte dalle «porte TCP aperte» che la
  // scansione vede da fuori: la differenza fra i due elenchi è la regola di
  // firewall, e un nome solo la cancellerebbe.
  listening_ports: t('Porte in ascolto'),
  established_connections: t('Connessioni stabilite'),
  // Kubernetes and private clouds (OpenStack, CloudStack).
  k8s_cluster: t('Cluster Kubernetes'),
  k8s_kind: t('Tipo di workload'),
  namespace: t('Namespace'),
  replicas_desired: t('Repliche previste'),
  replicas_ready: t('Repliche pronte'),
  service_type: t('Tipo di servizio'),
  cluster_ip: t('IP del cluster'),
  external_addresses: t('Indirizzi esterni'),
  hosts: t('Host'),
  ingress_class: t('Classe ingress'),
  ip_addresses: t('Indirizzi IP'),
  public_ips: t('IP pubblici'),
  // Oracle Database Appliance (appliances/oda.go).
  oda_model: t('Modello ODA'),
  oda_version: t('Versione ODA'),
  components: t('Componenti'),
  components_with_update: t('Componenti da aggiornare'),
  db_homes: t('DB home'),
  db_home_count: t('DB home'),
  db_unique_name: t('Nome univoco del database'),
  db_edition: t('Edizione'),
  db_role: t('Ruolo del database'),
  db_type: t('Tipo di database'),
  db_class: t('Classe del database'),
  db_shape: t('Forma del database'),
  db_storage: t('Archiviazione del database'),
  pdb_name: t('PDB'),
  character_set: t('Set di caratteri'),
  db_home: t('DB home'),
  db_home_location: t('Percorso della DB home'),
  time_zone: t('Fuso orario'),
  product: t('Prodotto'),
  cdb: t('Contenitore (CDB)'),
  // OLVM / oVirt (virt/olvm.go).
  manager: t('Gestito da'),
  compatibility_version: t('Livello di compatibilità'),
  committed_bytes: t('Spazio promesso alle VM'),
  domain_type: t('Tipo di dominio'),
  disk_provisioned_gb: t('Dischi assegnati (GB)'),
  disk_used_gb: t('Dischi occupati (GB)'),
  high_availability: t('Alta disponibilità'),
  stateless: t('Senza stato'),
  vm_type: t('Tipo di VM'),
  os_type: t('Sistema operativo dichiarato'),
  cpu_mhz: t('Frequenza CPU (MHz)'),
  libvirt_version: t('Versione libvirt'),
  host_type: t('Tipo di host'),
  olvm_id: t('Id OLVM'),
  cpu_type: t('Famiglia CPU del cluster'),
  data_center: t('Data center'),
  vlan_id: t('VLAN'),
  unix_sockets: t('Socket Unix in ascolto'),
  tls_certificates: t('Certificati TLS esposti'),
  storage_mapping_count: t('Mappature'),
  storage_mappings: t('Mappature'),
  storage_disk_count: t('Dischi'),
  storage_pool_count: t('Pool di storage'),
  storage_lun_count: t('LUN'),
  storage_disks: t('Elenco dischi'),
  storage_pools: t('Elenco pool'),
  storage_luns: t('Elenco LUN'),
  mgmt_addresses: t('Altri indirizzi di gestione'),
  storage_iqns: t('IQN (initiator iSCSI)'),
  storage_wwpns: t('WWPN (porte FC)'),
  storage_eth_port_count: t('Porte ethernet'),
  storage_eth_ports: t('Porte ethernet'),
  storage_host_count: t('Host registrati'),
  storage_hosts: t('Host registrati'),
  storage_initiator_count: t('Initiator'),
  storage_initiators: t('Initiator'),
  vm_count: t('Macchine virtuali ospitate'),
  software_rev: t('Versione software'),
  os_version: t('Versione OS'),
  firmware_version: t('Versione firmware'),
  chassis_id: t('Chassis ID'),
  sys_descr: t('Descrizione'),
  sys_location: t('Ubicazione'),
  sys_contact: t('Contatto'),
  hostname: t('Nome host'),
  dns_name: t('Nome DNS'),
  dns_domain: t('Dominio DNS'),
  detected_at: t('Rilevato il'),
  os: t('Sistema operativo'),
  container_runtime: t('Runtime container'),
  // Cloud: nomi che ricorrono in quasi tutte le classi del provider.
  provider: t('Provider'),
  region: t('Regione'),
  availability_zone: t('Zona di disponibilità'),
  instance_type: t('Tipo di istanza'),
  instance_class: t('Classe di istanza'),
  engine: t('Motore'),
  engine_version: t('Versione motore'),
  runtime: t('Runtime'),
  timeout_s: t('Timeout (s)'),
  endpoint: t('Endpoint'),
  scheme: t('Schema'),
  cidr_block: t('Blocco CIDR'),
  rule_count: t('Regole'),
  versioning: t('Versionamento'),
  encrypted: t('Cifrato'),
  publicly_accessible: t('Accessibile pubblicamente'),
  version: t('Versione'),
  // Base dati on-premise.
  port: t('Porta'),
  edition: t('Edizione'),
  patch_level: t('Livello di patch'),
  database_name: t('Nome del database'),
  db_endpoint: t('Endpoint'),
  db_ref: t('Riferimento database'),
  // Directory.
  windows_forest: t('Foresta'),
  ad_computer_count: t('Computer'),
  cluster_name: t('Nome del cluster'),
  cluster_fqdn: t('FQDN del cluster'),
  cluster_type: t('Tipo di cluster'),
  cluster_node_count: t('Nodi del cluster'),
  cluster_vips: t('Indirizzi VIP'),
  cluster_witness: t('Testimone'),
  cluster_quorum: t('Quorum'),
  capacity_gb: t('Capacità (GB)'),
  free_gb: t('Spazio libero (GB)'),
  used_percent: t('Spazio usato (%)'),
  daily_growth_bytes: t('Crescita al giorno'),
  days_to_full: t('Giorni al pieno'),
  function: t('Funzione'),
  note: t('Note'),
  // Il valore è reso in multipli binari da `formatBytes`: ripetere l'unita'
  // nell'etichetta ne dichiarerebbe una seconda, e le due si contraddirebbero.
  capacity_bytes: t('Capacità'),
  free_bytes: t('Spazio libero'),
  used_bytes: t('Spazio occupato'),
  provisioned_bytes: t('Spazio provisionato'),
  physical_capacity_bytes: t('Capacità fisica'),
  physical_free_bytes: t('Spazio fisico libero'),
  disk_bytes: t('Disco'),
  storage_type: t('Tipo di archiviazione'),
  filesystem: t('File system'),
  vmfs_version: t('Versione VMFS'),
  nfs_server: t('Server NFS'),
  nfs_export: t('Export NFS'),
  mount_path: t('Punto di mount'),
  // Con il thin provisioning lo spazio promettibile e quello fisico si muovono
  // in modo indipendente: due nomi distinti perché sono due misure distinte.
  physical_capacity_gb: t('Capacità fisica (GB)'),
  physical_free_gb: t('Spazio fisico libero (GB)'),
  physical_used_percent: t('Spazio fisico usato (%)'),
  allocated_gb: t('Spazio occupato (GB)'),
  cluster_count: t('Cluster collegati'),
  accessible: t('Accessibile'),
  shared: t('Condiviso fra host'),
  network_type: t('Tipo di rete'),
  vlan: t('VLAN'),
  vlan_mode: t('Modo VLAN'),
  vlan_trunk: t('VLAN del trunk'),
  switch: t('Commutatore'),
  switch_type: t('Tipo di commutatore'),
  host_count: t('Presente su host'),
  uplinks: t('Uplink'),
  uplink_count: t('Numero di uplink'),
  mtu: t('MTU'),
  port_count: t('Porte'),
  power_state: t('Stato di alimentazione'),
  guest_os: t('Sistema operativo'),
  cpu_count: t('vCPU'),
  cores_per_socket: t('Core per socket'),
  socket_count: t('Socket'),
  cpu_hot_add: t('Aggiunta CPU a caldo'),
  memory_mib: t('Memoria (MiB)'),
  memory_mb: t('Memoria (MB)'),
  memory_hot_add: t('Aggiunta memoria a caldo'),
  hardware_version: t('Versione hardware virtuale'),
  hardware_upgrade_policy: t('Aggiornamento hardware'),
  boot_firmware: t('Firmware di avvio'),
  provisioned_gb: t('Spazio provisionato (GB)'),
  disk_count: t('Dischi'),
  nic_count: t('Schede di rete'),
  cdrom_connected: t('CD-ROM collegati'),
  datastores: t('Datastore'),
  networks: t('Reti'),
  cluster: t('Cluster'),
  cluster_moref: t('Cluster (riferimento)'),
  esxi_version: t('Versione ESXi'),
  esxi_build: t('Build ESXi'),
  cpu_model: t('Modello CPU'),
  cpu_architecture: t('Architettura CPU'),
  cpu_cores: t('Core fisici'),
  cpu_threads: t('Thread'),
  drs_enabled: t('DRS attivo'),
  ha_enabled: t('HA attivo'),
  connection_state: t('Stato di connessione'),
  moref: t('Riferimento vCenter'),
  platform: t('Piattaforma'),
  api_family: t('Famiglia di API'),
  discovered_via: t('Raccolto tramite'),
  collected_by: t('Raccolto da'),
  collected_via: t('Raccolto via'),
  virtualization_detected_by: t('Virtualizzazione rilevata da'),
  software_managed_count: t('Pacchetti gestiti'),
  resource_type: t('Tipo di risorsa'),
  bios_uuid: t('UUID BIOS'),
  mgmt_ip: t('Indirizzo di gestione'),
  host: t('Host'),
  // Attributi delle righe di disco e di scheda di rete, che nel dettaglio
  // diventano intestazioni di tabella.
  label: t('Etichetta'),
  size_gb: t('Dimensione (GB)'),
  datastore: t('Datastore'),
  file: t('File'),
  provisioning: t('Provisioning'),
  disk_type: t('Tipo di disco'),
  device_id: t('Dispositivo'),
  format: t('Formato'),
  backing: t('Supporto'),
  mac_type: t('Tipo di MAC'),
  adapter_type: t('Tipo di scheda'),
  memory_gb: t('Memoria (GB)'),
  memory_bytes: t('Memoria'),
  cpu_sockets: t('Socket'),
  zone: t('Zona'),
  hypervisor: t('Hypervisor'),
  guest_platform: t('Piattaforma ospite'),
  architecture: t('Architettura'),
  state: t('Stato'),
  status: t('Stato osservato'),
  mac: t('MAC'),
  network: t('Rete'),
  adapter: t('Tipo di scheda'),
  connected: t('Collegata'),
  ip: t('Indirizzo IP'),
  prefix: t('Prefisso'),
  os_family: t('Famiglia del sistema'),
  open_tcp_ports: t('Porte TCP aperte'),
  reputation: t('Reputazione'),
  reputation_detail: t('Dettaglio reputazione'),
  reputation_checked_at: t('Reputazione controllata al'),
  cidr: t('Subnet (CIDR)'),
  address_count: t('Indirizzi contenuti'),
  reputation_by_address: t('Reputazione per indirizzo'),
  // La ricaduta darebbe «Winrm enabled»: leggibile, e sbagliata di nome
  // proprio. Vale la pena scriverlo, perché è l'attributo che dice **perché**
  // un host Windows non è stato inventariato.
  winrm_enabled: t('WinRM attivo'),
}

/**
 * Etichette accorciate per le intestazioni di colonna.
 *
 * Solo dove la forma lunga allarga la tabella: il resto usa la stessa etichetta
 * del dettaglio, perché due nomi per lo stesso dato sono un costo che si paga
 * ogni volta che si passa dall'elenco alla scheda.
 */
export const COLUMN_LABELS: Record<string, string> = {
  // In colonna le etichette lunghe allargano la tabella oltre lo schermo. Ma la
  // regola è **accorciare**, non rinominare: «Versione» è «Versione software»
  // detta corta, «Matricola» era un'altra parola per «Serial Number» — e
  // chi passava dall'elenco alla scheda doveva dedurre che fossero lo stesso
  // campo. Un'abbreviazione si riconosce, un sinonimo va imparato.
  software_rev: t('Versione'),
  os_version: t('Versione OS'),
  sys_location: t('Ubicazione'),
  capacity_gb: t('Capacità GB'),
  free_gb: t('Libero GB'),
  used_percent: t('Usato %'),
  storage_type: t('Tipo'),
  vmfs_version: t('VMFS'),
  network_type: t('Tipo rete'),
  switch_type: t('Tipo switch'),
  uplink_count: t('Uplink'),
  power_state: t('Stato'),
  memory_mib: t('Memoria MiB'),
  memory_mb: t('Memoria MB'),
  hardware_version: t('Hardware'),
  provisioned_gb: t('Provisionato GB'),
  hardware_upgrade_policy: t('Aggiorn. hardware'),
  connection_state: t('Connessione'),
}

/**
 * Etichetta leggibile di un attributo.
 *
 * La ricaduta non è un trattino: un attributo appena aggiunto da un connettore
 * si legge comunque — `vlan_trunk` diventa «Vlan trunk», che è imperfetto ma
 * dice di cosa si tratta, mentre un segnaposto nasconderebbe l'unica
 * informazione disponibile.
 */
export function attributeLabel(name: string): string {
  return ATTRIBUTE_LABELS[name] ?? humanize(name)
}

/** Come sopra, per l'intestazione di una colonna. */
export function columnLabel(name: string): string {
  return COLUMN_LABELS[name] ?? attributeLabel(name)
}

function humanize(name: string): string {
  return name.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
}

/**
 * Un valore singolo, scritto come si legge.
 *
 * Gli attributi arrivano da connettori diversi e non hanno un tipo dichiarato:
 * senza questa normalizzazione un booleano finirebbe reso «true» e un oggetto
 * «[object Object]» — che non è un errore, è peggio: una cella piena di niente
 * in una tabella che sembra funzionare.
 */
/**
 * Attributi il cui valore è in **byte**, e che vanno mostrati come si leggono.
 *
 * Il modello dati canonicalizza tutte le dimensioni in byte, perché è l'unica
 * unità verso cui si converte senza scegliere fra 1000 e 1024. La console fa il
 * passo opposto, e qui la scelta si può fare una volta sola e in modo visibile:
 * si usano i multipli binari — GiB, non GB — perché è quello che i produttori di
 * hypervisor e di dischi intendono quando scrivono «GB», e mostrare 8,59 dove
 * l'interfaccia di vCenter dice 8 farebbe dubitare del dato giusto.
 */
const BYTE_ATTRIBUTES = /(^|_)bytes$/

const BINARY_UNITS = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'] as const

/** Un numero di byte scritto come si legge. */
export function formatBytes(value: number): string {
  if (!Number.isFinite(value)) return String(value)
  // Un valore negativo è legittimo — la «crescita al giorno» di uno storage
  // che si sta svuotando — e si legge come il positivo, col segno davanti.
  if (value < 0) return `-${formatBytes(-value)}`
  let n = value
  let unit = 0
  while (n >= 1024 && unit < BINARY_UNITS.length - 1) {
    n /= 1024
    unit += 1
  }
  // Zero decimali sotto il KiB (un byte e mezzo non esiste), uno sopra: la
  // seconda cifra su una capacità in TiB è rumore che cambia a ogni scansione.
  const digits = unit === 0 ? 0 : n < 10 ? 1 : 0
  // Il separatore decimale della lingua scelta: «8,5 GiB» in italiano e in
  // francese, «8.5 GiB» in inglese. Senza raggruppamento: sotto 1024 non serve.
  const cifre = n.toLocaleString(localeCorrente(), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: false,
  })
  return `${cifre} ${BINARY_UNITS[unit]}`
}

/**
 * Un valore, formattato secondo quello che l'attributo **è**.
 *
 * Senza questa funzione la canonicalizzazione in byte avrebbe reso la console
 * peggiore di prima: una memoria da 8 GiB mostrata come 8589934592 è un dato
 * corretto e illeggibile, che è un modo diverso di essere sbagliato.
 */
export function attributeValue(name: string, value: unknown): string {
  if (typeof value === 'number' && BYTE_ATTRIBUTES.test(name)) return formatBytes(value)
  return scalar(value)
}

export function scalar(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? t('sì') : t('no')
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/**
 * Il valore di un attributo in una cella di tabella.
 *
 * Gli elenchi lunghi si troncano dicendo quanti ne restano: una cella con venti
 * uplink rende illeggibile l'intera riga, e il numero che manca si legge nella
 * scheda di dettaglio, dove c'è lo spazio.
 */
export function attributeCell(value: unknown, name = ''): string {
  if (Array.isArray(value)) {
    if (value.length === 0) return '—'
    const shown = value.slice(0, 3).map((v) => scalar(v))
    return value.length > 3 ? `${shown.join(', ')} +${value.length - 3}` : shown.join(', ')
  }
  return attributeValue(name, value)
}
